import { Injectable } from '@angular/core';
import { custom } from 'zod/v4';

/*
 * This service stores all static texts used in the CMS part of the application
 * */

type CmsTextRecord = any;

@Injectable({
  providedIn: 'root',
})
export class CmsTextProviderService {
  public readonly shared: CmsTextRecord = {
    all: 'Alle',
    cancel: 'Abbrechen',
    continue: 'Weiter',
    confirm: 'Bestätigen',
    accept: 'Verstanden',
    save: 'Speichern',
    keep: 'Übernehmen',
    icon: 'Icon',
    text: 'Text',
    title: 'Titel',
    type: 'Art',
    link: 'Link',
    login: 'Login',
    password: 'Passwort',
    search: 'Suche',
    show: 'Anzeigen',
    altText: 'Alternativ Text Button Bild',
    showBackgroundImage: 'Bild im Hintergrund anzeigen',
    enterPassword: 'Passwort eingeben',
    linkAddressRequired: 'Link-Adresse',
    email: 'E-Mail',
    enterEmail: ' E-Mail-Adresse eingeben',
    emailError: ' Email Adresse muss eine Email Adresse sein',
    description: 'Beschreibungstext',
    designPreview: 'Design Vorschau',
    shownTranslations: 'Angezeigte Übersetzungen:',
    lastEditedAt: 'Zuletzt bearbeitet',
    availableAttributes: 'Folgende Attribute stehen zur Verfügung',
    proofOfPurchaseNumber: '{{Rechnungsnummer}}',
    proofOfPurchaseDate: '{{Rechnungsdatum}}',
    languageError: 'Fehler bei der Sprachauswahl',
    languageSelection: 'Sprachauswahl',
    registration: 'Registrierung',
    shoppingCart: 'Warenkorb',
    general: 'Allgemein',
    upperArea: 'Oberer Abschnitt',
    lowerArea: 'Unterer Abschnitt',
    vatRate: 'MwSt',
    buttons: 'Buttons',
    language: 'Sprachen',
    dates: 'Datum- und Zeitangaben',
    abbreviation: 'Kürzel',
    incidentMonitor: 'Ereignis-Monitor',
    allImages: 'Bilder',
    upload: 'Hochladen',
    delete: 'Löschen',
    actions: 'Aktionen',
    creationDate: 'Erstellungsdatum',
    uploadedAt: 'Hochgeladen am',
    header: 'Header',
    onThisPage: 'Auf dieser Seite',
    foldEverything: 'Alles einklappen',
    banner: 'Banner',
    colours: 'Farben',
    layout: 'Layout',
    resetToDefault: 'Auf Werkseinstellungen zurücksetzen',
    fontColour: 'Schriftfarbe',
    backgroundColour: 'Hintergrundfarbe',
    hexColourValue: 'Farbwert (HEX)',
    image: 'Bild',
    footer: 'Footer',
    setActive: 'Aktiv setzen',
    corporateDesign: 'Corporate Design',
    salutations: 'Anreden',
    personalArea: 'Persönlicher Bereich',
    revertChanges: 'Änderungen zurücksetzen',
    startPage: 'Startseite',
    newSection: 'Neuer Abschnitt',
    section: 'Abschnitte',
    sectionNavigation: 'Abschnitt Navigation',
    titleOptional: 'Titel (optional)',
    appearance: 'Erscheinungsbild',

    resetChangesWarning:
      'Sie haben nicht gespeicherte Änderungen. Diese gehen beim Klick auf Weiter verloren. Möchten Sie dennoch fortfahren ohne die Änderungen zu übernehmen?',
    resetWarning:
      'Sie haben nicht gespeicherte Änderungen. Diese gehen beim Verlassen der Seite verloren. Möchten Sie die Seite dennoch verlassen ohne die Änderungen zu übernehmen?',
    resetToDefaultWarning:
      'Möchten Sie wirklich auf Werkseinstellungen zurücksetzen? Änderungen und vorher gespeicherte Angaben gehen dabei verloren.',
    accessibilityHeadline:
      'Das Icon weist auf wichtige Barrierefreiheitsanforderungen hin, um Inhalte für alle zugänglich zu machen:',
    accessibilityHints: [
      'Formulieren Sie aussagekräftig, klar und verständlich.',
      'Bildbeschreibungen müssen so formuliert sein, dass mithilfe von Assistenz-Werkzeugen alle relevanten Informationen zugänglich sind.',
      'Bilder mit Verlinkungen haben keine konkrete Bildbeschreibung, erklären Sie stattdessen wohin der Bild-Link führt.',
      ' Bei rein dekorativen Bildern sollte die Beschreibung leer gelassen werden. Das gilt auch, wenn relevante Informationen aus dem Bild in einem beistehenden Text wiederholt werden (Das Bild ist somit dekorativ.)',
    ],
    fontFamily: 'Schriftart',
    noDataFound: 'Keine Daten gefunden',
    add: 'Hinzufügen',
    predefined: 'Palette',
    custom: 'Benutzerdefiniert'
  };
  public readonly userFeedback: CmsTextRecord = {
    headline: 'Nutzerfeedback',
    code: 'Code',
    searchPlaceholder: 'Bezeichnung, Inhalt...',
  };
  public readonly ticket: CmsTextRecord = {
    orders: 'Bestellungen',
    timeFrame: 'Zeitraum',
    orderTickets: 'Buchungen',
    searchPlaceholder: 'E-Mail oder Bestellungs-ID',
  };
  public readonly sortiment: CmsTextRecord = {
    headline: 'Sortiment',
    ticketSortGroups: 'Fahrscheingruppen',
    searchPlaceholder: 'Suchen',
    sortPlaceholder: 'Sortieren',
    filterPlaceholder: 'Filter',
    filterType: 'Typ',
    filterStatus: 'Status',
    typePlaceholder: 'Typ auswählen',
    statusPlaceholder: 'Status auswählen',
    notSpecified: '- Nicht angegeben -',
    nameDescending: 'Name - absteigend',
    nameAscending: 'Name - aufsteigend',
    typeDescending: 'Typ - absteigend',
    typeAscending: 'Typ - aufsteigend',
    layoutIncomplete: 'Layouts unvollständig',
    resetAll: 'Alles zurücksetzen',
    layouts: 'Layouts',
    generalInformations: 'Allgemeine Informationen',
    displayPrice: 'Preis anzeigen',
    imageSource: 'Bildquelle',
    displayImage: 'Bild anzeigen',
    detailInformations: 'Detail Informationen',
    nameOfAdditionalDisplay: 'Erweiterte Schaltfläche Bezeichnung',
    nameOfTicketActionButton: 'Ticket Aktion Schaltfläche Bezeichnung',
    newDetails: 'Neue Details',
    description: 'Beschreibung',
    layoutAssignmentTitle: 'Layouts Fahrscheinen zuordnen',
  };
  public readonly allTexts: CmsTextRecord = {
    webHeadline: 'Web Texte',
    appHeadline: 'App Texte',
    searchPlaceholder: 'Value, EntityType, RelationType...',
    import: 'Importieren',
    export: 'Exportieren',
    source: 'Quelle',
    relationType: 'RelationType',
  };
  public readonly sideBar: CmsTextRecord = {
    monitoring: 'Monitoring',
    management: 'Verwaltung',
    collection: 'Sammlung',
    admin: 'Admin-Portal',
    orders: 'Bestellungen',
    users: 'Kunden',
    media: 'Medien',
    texts: 'Texte',
    globalComponents: 'Globale Komponenten',
    content: 'Inhalte',
    sortiment: 'Sortiment',
    general: 'Allgemein',
    customerAccount: 'Kundenkonto',
    other: 'Sonstige',
    startPage: 'Startseite',
    shoppingCart: 'Warenkorb',
    purchaseConfirmation: 'Kaufbestätigung',
    userFeedback: 'Nutzerfeedback',
    accessibility: 'Barrierefreiheit',
    relationSearch: 'Relationsauswahl',
    administration: 'Administration',
    shop: 'Shop',
    configuration: 'Konfiguration',
    customerPermissions: 'Kundenberechtigungen',
    announcementsMotD: 'Ankündigungen(MotD)',
    design: 'Gestaltung',
    shopEditor: 'Shop-Editor',
    statistic: 'Statistik',
    logs: 'Logs',
    elements: 'Elemente/Blocks',
    appearance: 'Erscheinungsbild',
  };
  public readonly shoppingCart: CmsTextRecord = {
    ticketOptions: 'Ticketoptionen',
    editTicketLabel: 'Fahrschein bearbeiten - Label',
    editTicketOwnerLabel: 'Fahrgastdaten bearbeiten - Label',
    ticketInfoLabel: 'Ticketinformationen - Label',
    deleteTicketLabel: 'Ticket löschen - Label',
    ticketAmountLabel: 'Label Anzahl',
    showVat: 'MwSt. anzeigen',
    headlineLabel: 'Warenkorb-Überschrift',
    priceLabel: 'Label Preis',
    ticketsLabel: 'Label Tickets',
    netPriceLabel: 'Label Netto Preis',
    vatRateLabel: 'Label MwSt.',
    continueLabel: 'Weiter-Button-Label',
    altButtonLabel: 'Alternativ-Button-Label',
    finishPurchaseLabel: 'Abschließen-Button-Label',
    emptyCartLabel: 'Warenkorb-Leer-Text',
  };
  public readonly language: CmsTextRecord = {
    standardLanguage: 'Standardsprache',
    standardLanguageExplanation:
      'Die Standardsprache ist die vorausgewählte Sprache beim ersten Aufruf des Webshops. Falls keine Übersetzung für einen Text hinterlegt ist, wird der Text in der Standardsprache angezeigt.',
    german: 'Deutsch (Alltagssprache) (DE)',
    germanExplanation:
      'Deutsch ist immer aktiv. Bei fehlenden Übersetzungen, wird der deutsche Begriff im Webshop angezeigt.',
    languageExplanation:
      'Sprachen sind in ihrem Shop nur wählbar, wenn die Sprache hier aktiviert ist. Stellen Sie sicher, dass der Übersetzungsstatus ausreichend ist, bevor Sie die Sprache in Ihrem Shop aktivieren.',
    translationStatus: 'Übersetzungsstatus',
    addLanguage: 'Neuen Übersetzungssprache hinzufügen',
  };
  public readonly incidentMonitor: CmsTextRecord = {
    searchPlaceholder: 'Code oder Nachricht',
    message: 'Nachricht',
  };
  public readonly allImages: CmsTextRecord = {
    searchPlaceholder: 'Suchen',
    uploadExplanation:
      'Per Drag-and-Drop können Bilder hochgeladen werden. Einfach die Datei über die Tabelle ziehen.',
    imageDescription: 'Bildbezeichnung',
    uploadHint: 'Datei an beliebiger Stelle loslassen, um sie hochzuladen.',
    maxSize: 'Maximale Dateigröße:',
    usage: 'Verwendung',
    alreadyInUse: 'Das Bild ist in Verwendung und kann nicht gelöscht werden',
    uploadInstruction:
      'Laden Sie hier alle Bilder hoch, die Sie in Ihrem Webshop verwenden möchten.',
  };
  public readonly header: CmsTextRecord = {
    backgroundColourExplanation:
      'Sie können hier die Hintergrundfarbe, sowie die Schriftfarbe des Headers, selbst bestimmen. Achten Sie auf ausreichend Kontrast zwischen der der Hintergrundfarbe und der darüber liegenden Schrift.',
    button: 'Startseite - Button',
    headerImageExplanation:
      'Im Header können Sie über das hier angegebene Bild, als Button, zur Startseite gelangen.',
    preview: 'Vorschau: Title',
    fontSize: 'Schriftgröße (in der Einheit pt)',
    additionalImages: 'Weitere Bilder',
    additionalImagesInfo:
      'Fügen Sie Bilder am linken oder rechten Rand des Headers hinzu. Diese Bilder werden in der mobilen Ansicht nicht angezeigt.',
    imageLeft: 'Bild links',
    altTextLeft: 'Alternativ Text Bild Links',
    imageRight: 'Bild rechts',
    altTextRight: 'Alternativ Text Bild Rechts',
  };
  public readonly footer: CmsTextRecord = {
    block1: 'Block 1 - Bilder',
    title: 'Titel (optional)',
    description: 'Beschreibung',
    linkAddress: 'Link-Adresse (optional)',
    addEntry: 'Neuen Eintrag hinzufügen',
    block2: 'Block 2 - Freitext',
    freeText: 'Freitext',
    block3: 'Block 3 - Zahlungsarten',
    block4: 'Rechntlicher Bereich',
    linkText: 'Link-Text',
    linkCannotBeRemoved: 'Dieser Link kann nicht entfernt werden.',
    lightMode: 'Heller Modus',
    darkMode: 'Dunkler Modus',
    font: 'Schrift',
    background: 'Hintergrund',
    imageSource: 'Bildquelle',
    visible: 'Sichtbar',
    hidden: 'Ausgeblendet',
    alwaysVisible: 'immer sichtbar',
    block: 'Block',
    legalArea: 'Rechtlicher Bereich',
    type: 'Typ',
    logo: 'Logo',
    paymentMethods: 'Zahlungsarten',
    paymentProvider: 'Zahlungsanbieter',
    links: 'Links',
    linkAddressLabel: 'Link-Adresse',
    emptyRow: 'Leere Zeile (Block hier ablegen)',
    hiddenBlocks: 'Ausgeblendete Blöcke',
    noHiddenBlocks: 'Keine ausgeblendeten Blöcke',
    information: {
      prefix:
        'Sie können hier die Hintergrundfarbe Ihres Footers selbst bestimmen. Die Schriftfarbe können Sie in den Einstellungen im Bereich',
      linkText: 'Corporate Design',
      suffix:
        'vornehmen. Achten Sie auf ausreichend Kontrast zwischen der Hintergrundfarbe und der darüber liegenden Schrift.',
    },
  };
  public readonly configuration: CmsTextRecord = {
    amconApp: 'AMCON.APP',
    amconWeb: 'AMCON.WEB',
    products: 'Produkte',
    basic: 'Basis',
    startPageInfo:
      'Ein- und Ausblenden von der Startseite, mit Kacheln für die Navigation zu Unterseiten',
    loginButtonInfo: 'Login-Button ein- oder ausblenden. Standard = aktiv.',
    register: 'Registrierung',
    journeyPlanning: 'Fahrplanauskunft',
    operatorMapping: 'Operator Mapping',
    addOperatorMapping: 'Operator Mapping Hinzufügen',
    createOperatorMapping: 'Operator Mapping Erstellen',
    editOperatorMapping: 'Operator Mapping Bearbeiten',
    deleteOperatorMapping: 'Operator Mapping Löschen',
    deleteOperatorMappingWarning:
      'Wollen Sie das Operator Mapping {{operatorName}} mit der Id {{operatorId}} wirklich löschen?',
    subscriptionPrefix: 'Abo-',
    subscriptionInfoStart:
      'Registrieren-Button ein- oder ausblenden. Standard = aktiv. <br/> Wenn die ',
    subscriptionInfoEnd:
      'weiter unten gesetzt ist, ist die Registrierung immer aktiv.',
    existingCustomerRegistration: 'Bestandskundenregistrierung',
    minimumRegistrationAge: 'Mindestalter für Registrierung',
    documents: 'Dokumente',
    documentShipping: 'Dokumentenversand',
    documentInfo:
      'Verwalte die Dokumente, die bei jeder Bestellbestätigung mit versendet werden sollen. (Maximale Dateigröße: XXX MB)',
    appButton: 'App-Button',
    appButtonInfo: 'Erklärung, was mit App-Button gemeint ist.',
    clientId: 'Mandanten ID (GUID)',
    clientIdInfo:
      'Die Mandaten-GUID finden Sie im Hintergrundsystem unter Benutzer/Mandanten.',
    addressValidation: 'Addressvalidierung',
    addressValidationInfo:
      'Für die Adressvalidierung muss im AMCON.System eine Adressdatenbank hinterlegt werden.',
    directSale: 'Direktverkauf',
    directSaleInfo: 'Aktiviert die Funktion des Direktverkaufs.',
    guestSale: 'Gastkauf',
    serviceArea: 'Bediengebiet',
    serviceAreaId: 'Bediengebiet ID',
    relationSearch: 'Relationssuche',
    fixPriceSale: 'Festpreis-Verkauf',
    paymentProvider: 'Zahlungsdienstleister',
    discountFunctions: 'Rabattfunktion',
    wallet: 'Wallet',
    walletInfo:
      'Aktiviert Wallet-Funktionen. In der App werden hierdurch auch die Buttons für die Wallet aktiviert.',
    subscriptionLicense: 'Abo-Lizenz',
    subscriptionInfo: 'Aktiviert die Funktion des Aboverkaufs',
    categories: 'Kategorien',
    netRelation: 'Netzrelation',
    shibbolethProvider: 'Shibboleth-Provider',
    germanyTicketInfo: 'Deutschlandticket Anzeige der Preisstufe',
    cancellationWithoutLogin: 'Kündigung ohne Login',
    directSaleLicense: 'Direktverkauf-Lizenz',
    alternativePassengerData: 'Alternative Passagierdaten',
    proofOfEntitlement: 'Ausgabe Berechtigungen',
    discountLicense: 'Rabattfunktion-Lizenz',
    walletLicense: 'Wallet-Lizenz',
    shibboleth: 'Shibboleth',
    configuration: 'Konfiguration & Lizenzierung',
    licenseInfo:
      'Aktiviere die Produkte, die der Kunde lizenziert hat. Danach werden von der Lizenz abhängige erweiterte Einstellungen konfigurierbar. <br /> Falls nachträglich ein Produkt hinzukommt, achte darauf, die erweiterten Einstellungen vorzunehmen. Die Einstellungen gelten grundsätzlich für alle Produkte, falls nicht sind diese Einstellungen gekennzeichnet.',
    onDemandJourneyProvider: 'Anbieter',
    onDemandSortLabel: 'Produkt Bedarfsverkehr',
    comfortSortLabel: 'Produkt Komfortzuschlag',
    publicTransportProductGroupLabel: 'Produktgruppe ÖPNV-Teil',
    noSortSelected: 'Keine Auswahl',
    operatorId: 'OperatorId',
    dropDownOptions: 'Werte für Dropdown',
    dropDownOptionName: 'Option',
    addDropDownOption: 'Dropdown Wert hinzufügen',
    apiKey: 'API_KEY',
    apiSecret: 'API_SECRET',
    paymentClientId: 'Client ID',
    merchantId: 'Merchant ID',
    url: 'URL',
    baseUrl: 'Base URL',
    apple: 'Apple',
    google: 'Google',
    receiverEmail: 'Empfänger E-Mail Adresse',
    receiverName: 'Empfänger Name',
    receiverSubject: 'Betreff',
    providerUrl: 'Provider Url',
    maxShoppingCartSize: 'Maximale Anzahl von Tickets im Warenkorb',
    assignMethodOfProof: 'Berechtigungsnachweis pro Tarifgebiet zuweisen',
    tariffArea: 'Tarifgebiet',
    typeOfEntitlement: 'Berechtigungsart',
    chooseEntitlement: 'Berechtigung auswählen',
    tariffAreaInfo:
      'Tariffgebiet auswählen um Berechtigungsart zu konfigurieren.',
    onTariffAreaSelected: 'Ein Tarifbereich ausgewählt',
    tariffAreasSelected: ' Tarifbereiche ausgewählt',
    alternatePassengerDataInfo:
      'Aktiviert die Funktion, dass im Bestellprozess abweichende persönliche Daten angegeben werden können. Konkret, damit jemand für eine andere Person ein Ticket kaufen kann. Unter den erweiterten Einstellungen muss je Sorte und Tarifgebiet die Felderkonfiguration vorgenommen werden. ',
    advancedSettings: 'Erweiterte Einstellungen',
    salutation: 'Anrede',
    firstName: 'Vorname',
    lastName: 'Nachname',
    dateOfBirth: 'Geburtsdatum',
    sort: 'Sorte',
    editMedia: 'Medium bearbeiten',
    oneSortChosen: 'Eine Sorte ausgewählt',
    sortsChosen: ' Sorten ausgewählt',
    mailNotification: 'E-Mail-Benachrichtigung',
    mailNotificationFirstStep:
      '1. Templates für alle Vorgänge in Notify anlegen.',
    mailNotificationSecondStep:
      '2. Je Vorgang die ID (GUID) aus der Template Übersicht in Notify kopieren und hier eintragen.',
    mailNotificationThirdStep:
      '3. Die IDs müssen exakt übereinstimmen, damit E-Mail-Benachrichtigungen mit den gewünschten Templates für den Vorgang gesendet werden können.',
    setting: 'Einstellung',
    chooseWhichProduct:
      'Von welchem Produkt soll der Wert für beide übernommen werden?',
    expandAll: 'Alle ausklappen',
    collapseAll: 'Alle einklappen',
    showProducts: 'Produkte anzeigen',
    appliesToBothProducts: 'Gilt für beide Produkte - Verknüpfung aufheben',
    separateValues: 'Getrennte Werte - Verknüpfung aufheben',
    webLogoAlt: 'AMCON.WEB Logo',
    appLogoAlt: 'AMCON.APP Logo',
    openInNewTab: 'in neuem Tab öffnen',
    unavailableForProduct: 'Für dieses Produkt nicht verfügbar',
    separateForWebAndApp: 'für WEB und APP trennen',
    mergeForWebAndApp: 'für WEB und APP zusammenführen',
    cannotSeparateProducts: 'kann nicht für WEB und APP getrennt werden',
    cannotMergeProducts: 'kann nicht für WEB und APP zusammengeführt werden',
    bothProductsUnavailable:
      'Die Einstellung ist für beide Produkte nicht verfügbar und kann deshalb nicht getrennt werden.',
    oneProductUnavailable:
      'Die Einstellung ist nur für eines der beiden Produkte verfügbar und kann deshalb nicht zusammengeführt werden.',
  };
  public readonly banner: CmsTextRecord = {
    image: 'Bannerbild',
    imageInfo:
      'Mit einem Bannerbild können Sie Aufmerksamkeit generieren und Ihren Shop  bewerben. Bilder sorgen für größere Datenmengen, was bei Handy-Nutzern zu längeren Ladezeiten und einem erhöhten Datenverbrauch führt.',
    heightInPixels: 'Höhe in Pixel',
    description: 'Beschreibung',
    advertisementText: 'Werbetext',
    advertisementInfo:
      'Wählen Sie ein Bild, auf dem Ihr Text gut lesbar ist. Oder verwenden Sie eine Hintergrundform, um den Text vom Bannerbild abzuheben. Achten Sie auf ausreichend Kontrast zwischen Text und Hintergrund.',
    fontSize: 'Schriftgröße (in der Einheit pt)',
    subTitle: 'Untertitel',
    verticalAlign: 'Vertikale Ausrichtung',
    horizontalAlign: 'Horizontale Ausrichtung',
  };
  public readonly account: CmsTextRecord = {
    payment: 'Zahlungsarten',
    accountIcon: 'Konto-Icon',
    photoLabel: 'Foto - Platzhalter - Icon',
    loginButtonIcon: 'Login-Button - Icon',
    logoutButtonIcon: 'Logout-Button - Icon',
    salutationValues: 'Anrede-Werte',
    salutationValue: 'Anrede-Wert',
    addSalutation: 'Anrede hinzufügen',
  };
  public readonly orderOverview: CmsTextRecord = {
    actions: 'Aktionen',
    date: 'Datum',
    time: 'Zeit',
    payment: 'Bezahlmethode',
    comment: 'Kommentar',
    paymentType: 'Bezahltyp',
    email: 'E-Mail',
    orderId: 'Bestellungs-ID',
    userId: 'Benutzer-ID',
    ticketId: 'Buchungs-ID',
    sum: 'Bestellsumme',
    client: 'Mandant',
    status: 'Status',
    number: 'Nummer',
    price: 'Bruttobetrag',
    ticketName: 'Ticketname',
    start: 'Start',
    end: 'Ziel',
    via: 'Über',
    type: 'Art',
    vatRate: 'MwSt-Satz',
    cancelled: 'Storno-ID',
    infos: 'Zusätzliche Informationen',
    travellers: 'Reisende',
    detailsHeadline: 'Detaillierte Informationen',
  };
  public readonly customTiles: CmsTextRecord = {
    editTile: 'Karte bearbeiten',
    edit: 'Anpassen',
    setTileActive: 'Karte aktiv setzen',
    linkText: 'Link Text',
    linkAddress: 'Link-Adresse',
    addNewTile: 'Neue Karte hinzufügen',
    cannotRemoveSection: 'Der Abschnitt Navigation kann nicht entfernt werden.',
    deleteSection: 'Gesamten Abschnitt löschen',
    cannotRemoveTile: 'Diese Karte kann nicht entfernt werden',
  };
  public readonly layout: CmsTextRecord = {
    ticketLayout: 'Ticketlayout',
    title: 'Layouts',
    createdAt: 'Hochgeladen am',
    name: 'Name',
    upload: 'Layout Hochladen',
    export: 'Verkaufsdaten exportieren',
    uploadHint:
      'Per Drag-and-Drop können .rdlx Dateien hochgeladen werden. Einfach die Datei\n' +
      'über die Tabelle ziehen.',
    dragAndDrop: 'Datei an beliebiger Stelle loslassen, um sie hochzuladen.',
    fileSize: 'Maximale Dateigröße : 10 MB', // Directly bound to settings made in main.ts for uploading files
  };

  public readonly relationSearch: CmsTextRecord = {
    byDay: 'Tagesscharf',
    byMinutes: 'Minutenscharf',
    accuracy: 'Relationssuche-Genauigkeit',
    stopLimit: 'Limitierung Haltestellen',
    loadAllStops: 'Lade alle Haltestellen ohne Eingabe',
  };

  public readonly journeyPlanner: CmsTextRecord = {
    description:
      'Wenn keine Fahrplanauskunft freigeschaltet ist, wird standardmäßig über den Tarif gesucht.',
    headline: 'EFA (Elektronische Fahrplanauskunft)',
    baseUrl: 'Routenplaner Url',
    apiUrl: 'API Url',
    redirectToggleLabel: 'Weiterleitung an Fahrplanauskunft (VAO)',
    redirectDescription:
      'Diese Einstellung ist derzeit eine Sonderlösung für die VKG und schaltet die Weiterleitung zum VAO Routenplaner an.',
    relationInfoLabel: 'Verbindungsauskunft',
    relationSearchWarning:
      'Die Fahrplanauskunft ist aktiv! Die bestehende Konfiguration der Relationssuche wird ignoriert.',
  };

  public readonly ticketCategories: CmsTextRecord = {
    pickTicketsByMultiSelect:
      'Bitte wählen Sie die Tickets aus, die der Kategorie zugewiesen werden sollen.',
    designation: 'Bezeichnung',
    ticketCategories: 'Ticket-Kategorien',
    directTicketCategories: 'Direkt-Ticket-Kategorien',
    relationTicketCategories: 'Relations-Ticket-Kategorien',
    noTicketsFound: 'Keine Tickets gefunden',
    categories: 'Kategorien',
    assignedTickets: 'Zugewiesene Tickets',
    dragAndDrop: 'Per Drag & Drop',
    or: 'oder',
    fromList: 'Aus Liste hinzufügen...',
    categoryName: 'Kategoriename',
    assignTickets: 'Tickets zuweisen',
    rename: 'Umbenennen',
    switchToRight: 'Nach rechts verschieben',
    switchToLeft: 'Nach links verschieben',
    noCategories: 'Es wurden noch keine Kategorien angelegt.',
    errorTicketCategory: 'Fehler beim Kategoriename',
    headline: 'Ticket-Kategorien',
    addCategory: 'Kategorie hinzufügen',
  };

  public readonly userOverview: CmsTextRecord = {
    headline: 'Benutzer',
    firstName: 'Vorname',
    lastName: 'Name',
    email: 'E-Mail',
    status: 'Status',
    userId: 'Benutzer-ID',
    searchPlaceholder: 'Name, E-Mail oder Benutzer-ID',
  };

  public readonly license: CmsTextRecord = {
    headline: 'Lizenzen',
    licenseDesignation: 'Lizenz-Bezeichnung',
    web: 'WEB',
    app: 'APP',
  };

  public readonly appearanceText: CmsTextRecord = {
    headline: 'Erscheinungsbild',
    instructions:
      'Legen Sie das Aussehen und die Farben von Schaltflächen und Bedienelementen fest.',
    favHeader: 'Favicon',
    favHint: 'Symbol-Bild, welches als Vorschau im Browser-Tab angezeigt wird.',
    uploadImage: 'Bild hochladen',
    browserTabHeader: 'Browser-Tab',
    browserTabPreviewHeader: 'Browser-Tab Icon & Titel',
    browserTabName: 'Name des Browser-Tabs',
    elementForm: 'Elementform & Primärfarbe',
    corporateDesignUrl: '/cms/corporate-design',
    linkToCorporateDesign: 'Im Corporate Design anpassen',
    openLinkIcon: 'open_in_new',
    corporateDesignHint:
      'Die Primärfarbe wird als Standard-Füllfarbe für Bedienelemente verwendet. Änderungen an der Primärfarbe wirken sich auf alle Bedienelemente aus, sofern keine individuelle Farbe vergeben wurde.',
    controls: 'Standard-Bedienelemente',
    controlsColours: 'Farben',
    controlsColoursHint:
      'Legt die Farben für Buttons, Schalter, Checkboxen, Eingabefelder und andere Bedienelemente fest. Die Füllfarbe übernimmt automatisch die Primärfarbe aus dem Corporate Design – hier können Sie sie bei Bedarf anpassen.',
    controlsLightMode: 'Heller Modus',
    controlsDarkMode: 'Dunkler Modus',
    controlsAction: 'Aktion',
    controlsSecondaryAction: 'Sekundäre Aktion',
    controlsTertiaryAction: 'Tertiäre Aktion',
    controlsLabel: 'Label',
    controlsInput: 'Input',
    controlsCheckbox: 'Checkbox',
    controlsRadio: 'Radio',
    controlsSwitch: 'Switch',
    controlsFillColour: 'Füllfarbe',
    controlsFillColourHint:
      'Verwendet standardmäßig die Primärfarbe aus dem Corporate Design. Hier können Sie eine abweichende Farbe wählen.',
    controlsContrast: 'Kontrast (Text/Icon)',
    controlsBorder: 'Umrandung (gefüllter Button)',
    controlsBorderHint:
      'Optional: Ergänzt den gefüllten Button um einen sichtbaren Rand – nützlich, wenn die Füllfarbe wenig Kontrast zum Seitenhintergrund hat.',
    controlsOutline: 'Outline-Stil',
    controlsOutlineHint:
      'Wird für Outlined- und Ghost-Buttons sowie die Umrandung von Eingabefeldern im aktiven Zustand verwendet.',
    ctaHeader: 'Hervorgehobene Aktionen (CTA)',
    ctaColours: 'Farben',
    ctaColoursHint:
      'Für Aktionen, die besonders auffallen sollen – z. B. „Jetzt kaufen“, „Bestellen“ oder „Registrieren“. Nutzen Sie eine eigene Farbe, um sie von Standard-Buttons abzuheben.',
    ctaPrimaryAction: 'Primäre CTA',
    ctaSecondaryAction: 'Sekundäre Aktion',
    ctaFillColour: 'Füllfarbe',
    ctaFillColourHint:
      'Wählen Sie eine auffällige Farbe, die sich von der Standard-Füllfarbe abhebt.',
    ctaContrast: 'Kontrast (Text/Icon)',
    ctaBorder: 'Umrandung (gefüllter Button)',
    ctaBorderHint:
      'Optional: Ergänzt den gefüllten Button um einen sichtbaren Rand – nützlich, wenn die Füllfarbe wenig Kontrast zum Seitenhintergrund hat.',
    ctaOutline: 'Outline-Stil',
    ctaOutlineHint: 'Wird für Outlined- und Ghost-Buttons verwendet.',
    fontHeader: 'Schriftart',
    fontHint:
      'Die ausgewählte Schriftart wird für alle Texte im Webshop verwendet.',
    fontDefaultName: 'Barlow',
    fontActiveLabel: 'Aktive Schriftart:',
    fontUpload: 'Schriftart hochladen',
    fontChange: 'Schriftart ändern',
    fontApply: 'Schriftart anwenden',
    fontActive: 'Aktiv',
    fontRestoreDefault: 'Standard wiederherstellen',
    fontDropHint: 'Dateien per Drag and Drop in dieses Feld ziehen.',
    fontPreviewUppercase: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ Ä Ö Ü',
    fontPreviewLowercase: 'abcdefghijklmnopqrstuvwxyz ß ä ö ü',
    fontPreviewSymbols: '0123456789 . : ; „ “ ´ ( ! ? ) + - * / = ° €',
    fontPreviewSample:
      'Zwölf Boxkämpfer jagen Viktor quer über den großen Sylter Deich. 0123456789',
    fontInvalidTypeError:
      'Fehler beim Hochladen. Es können nur TTF- und WOFF2-Dateien verwendet werden.',
    fontTooLargeError:
      'Fehler beim Hochladen. Datei zu groß. Bitte erneut versuchen.',
    fontUploadError:
      'Fehler beim Hochladen. Bitte prüfen Sie die Datei und versuchen Sie es erneut.',
    fontApplyError:
      'Die Schriftart konnte nicht angewendet werden. Bitte erneut versuchen.',
    fontRestoreError:
      'Die Standardschrift konnte nicht wiederhergestellt werden. Bitte erneut versuchen.',
    fontResetTitle: 'Standard wiederherstellen',
    fontResetDescription:
      'Durch das Wiederherstellen des Standards wird die aktuelle Schriftart entfernt.',
    fontResetHint:
      'Wenn Sie eine andere verwenden möchten, müssen Sie eine neue hochladen.',
    fontRestore: 'Wiederherstellen',
    uploadFont: 'upload',
  };

  public readonly corporate: CmsTextRecord = {
    headline: 'Corporate Design',
    instructions:
      'Legen Sie das grundlegende Aussehen des Shops fest. Auf den Unterseiten können Sie bestimmte Elemente anpassen.',
    colorPalette: 'Farbpalette',
    editColors: 'Farben bearbeiten',
    paletteDialogTitle: 'Farbpalette verwalten',
    paletteDialogDescription:
      'Hier verwalten Sie Ihre Farbpalette. Diese Farben stehen überall im CMS als Schnellauswahl zur Verfügung.',
    hexValue: 'Farbwert (HEX)',
    invalidHexValue: 'Geben Sie einen gültigen Wert ein',
    colorName: 'Name',
    colorNameInvalid: 'Der Name ist bereits vergeben.',
    primary: 'Primär',
    primaryColorInfo:
      'Die Primärfarbe ist die Standardfarbe für Bedienelemente der Benutzeroberfläche. Unter Erscheinungsbild können Sie bestimmte Elemente anpassen (Textfarbe, Outline-Stil, dunklen Modus usw.).',
    pickColor: 'Farbe auswählen',
    setPrimary: 'Als Primärfarbe verwenden',
    deleteColorTitle: 'Primärfarbe löschen',
    deleteColorConfirm: 'Möchten Sie die Farbe „{{colorName}}“ wirklich löschen? Die Farbe wird aus der Schnellauswahl entfernt und alle bestehenden Verweise gehen unwiderruflich verloren.',
    deleteColorQuestionStart: 'Möchten Sie die Farbe',
    deleteColorQuestionEnd: 'wirklich löschen?',
    deleteColorWarning:
      'Die Farbe wird aus der Schnellauswahl entfernt und alle bestehenden Verweise gehen unwiderruflich verloren.',
    discardPaletteChanges: 'Änderungen verwerfen',
    discardPaletteChangesQuestion:
      'Möchten Sie die Änderungen wirklich verwerfen?',
    discardPaletteChangesWarning:
      'Alle nicht gespeicherten Änderungen gehen verloren.',
    discard: 'Verwerfen',
    addColor: 'Farbe hinzufügen',
    emptyPaletteTitle: 'Noch keine Farben vorhanden.',
    emptyPaletteDescription:
      'Klicken Sie Farbe hinzufügen um Ihre Palette zu erstellen.',
    confirm: 'Bestätigen',
    amconBlue: 'AMCON Blau',
    amconGray: 'AMCON Grau',
    black: 'Schwarz',
    white: 'Weiß',
    primaryColor: 'Primärfarbe',
    changePrimaryColor: 'Primärfarbe ändern',
    paletteTab: 'Palette',
    customTab: 'Benutzerdefiniert',
    saveInPalette: 'In Palette speichern',
    customColorName: 'farbe',
    customColorNotSavedInfo:
      'Ohne Speichern wird die Farbe nur an dieser Stelle verwendet. Sie taucht nicht in der Palette auf und ist anderswo nicht als Schnellauswahl verfügbar.',
    standalonePrimaryColorInfo:
      'Die benutzerdefinierte Farbe wird nur an dieser Stelle verwendet. Sie taucht nicht in der Palette auf und ist anderswo nicht als Schnellauswahl verfügbar.',
    primaryColorHint:
      'Standardfarbe für Bedienelemente der Benutzeroberfläche. Kann an einzelnen Stellen individuell angepasst werden.',
    elementShape: 'Form von Elementen',
    square: 'Eckig',
    round: 'Rund',
    custom: 'Individuell',
    customRounding: 'Individuelle Eckenrundung',
    increaseRounding: 'Eckenrundung erhöhen',
    decreaseRounding: 'Eckenrundung verringern',
    elementShapeHint:
      'Bestimmt, ob die Ecken von Oberflächen und Buttons eckig, abgerundet oder ganz rund sein sollen.',
    changePrimaryHint: 
      'Nach dem Klick auf “Speichern” wird die Primärfarbe auf die Bedienelemente der Benutzeroberfläche angewandt. In der Schnellauswahl steht sie an erster Stelle. Die Sortierung lässt sich in der Farbpalette ändern.\n\nDie bestehende Färbung der Bedienelemente wird überschrieben. Anpassungen können unter „Erscheinungsbild“ vorgenommen werden.'
  };
}
